import {
  type OfferedValues,
  settingValue,
  valueAttribute,
  valueFromAttribute,
} from "./control.js";

/**
 * The settings panel, which is already on the page.
 *
 * Radio groups rather than dropdowns, because every choice here is worth
 * seeing without a click: somebody who does not know that pinyin has a word
 * spacing rule will not go looking in a `<select>` for it, but they will read
 * "Nánjīng Shì, not Nán jīng shì" sitting on the page.
 *
 * The markup is the standard hidden-radio-and-label pattern, written by
 * `partials/tools/pinyin-settings.html` and styled in `modules/tools.scss`.
 * This module used to build all of it on arrival, which is 22 radios, 9
 * fieldsets, 9 legends and 22 choice labels that every page already knew the
 * shape of. What is left is the two things that genuinely need a browser:
 * which radio is checked when the settings are not the defaults, and what to
 * do when one is clicked.
 */

/**
 * Check the radios that match the settings, where they are not already.
 *
 * The page ships with the defaults checked, so this moves nothing for a first
 * visit and moves one radio per field the last visit changed or the link
 * named. A field with no fieldset in the page is skipped rather than
 * reported: which settings a template offers is the template's business, and
 * a panel showing eight of the nine is a page that works.
 */
export function showSettings(container: Element, settings: object): void {
  for (const control of container.querySelectorAll<HTMLElement>(
    "[data-field]",
  )) {
    // Selected by the attribute, so every control names a field.
    const chosen = settingValue(settings, control.dataset["field"]!);

    if (chosen === undefined) {
      continue;
    }

    for (const radio of control.querySelectorAll<HTMLInputElement>(
      "input[type=radio]",
    )) {
      radio.checked = radio.value === valueAttribute(chosen);
    }
  }
}

/**
 * Report every change a visitor makes in the panel.
 *
 * The fields are passed in rather than reached for, so that this knows how to
 * read a setting without knowing which settings there are. The list it is
 * called with is `optionFields`.
 *
 * One listener on the container rather than one per radio, which is what lets
 * the panel be markup the script never owns: there is nothing here to tear
 * down and nothing that would have to be attached again.
 */
export function wireControls<Settings extends object>(
  container: Element,
  fields: readonly OfferedValues[],
  onChange: (changed: Partial<Settings>) => void,
): void {
  container.addEventListener("change", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    const field = input.closest<HTMLElement>("[data-field]")?.dataset["field"];
    const offered = fields.find((each) => each.field === field);
    if (offered === undefined) {
      return;
    }

    const value = valueFromAttribute(offered, input.value);
    if (value !== undefined) {
      // The field is one of the settings' own by construction, which is
      // exactly what a computed key cannot say for itself.
      onChange({ [offered.field]: value } as Partial<Settings>);
    }
  });
}
